import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { gql } from 'apollo-angular';
import { QueryManagerService } from 'finzzle-angular-utils';
import { OuiSelectOption } from 'omnium-ui/form-field';
import { MotifAnnulation, MotifRejet, Operation } from 'projects/box-lib/src/lib/models/generated/graphql';
import { firstValueFrom } from 'rxjs';
import { OptionsService } from '../../services/options.service';

const cancelOperations = gql`
  mutation cancelOperations($operationIds: [Int!]!, $commentaire: String!, $motifAnnulation: MotifAnnulation!) {
    cancelOperations(operationIds: $operationIds, comment: $commentaire, motifAnnulation: $motifAnnulation) {
      id
      statutId
      statut {
        id
        consultantLibelle
        backOfficeLibelle
      }
      activeOperationStateTransitionTriggers
      operationActionRights
    }
  }
`;

const refuseOperations = gql`
  mutation refuseOperations($operationIds: [Int!]!, $commentaire: String!, $motifRejet: MotifRejet!) {
    refuseOperations(operationIds: $operationIds, comment: $commentaire, motifRejet: $motifRejet) {
      id
      statutId
      statut {
        id
        consultantLibelle
        backOfficeLibelle
      }
      activeOperationStateTransitionTriggers
      operationActionRights
    }
  }
`;

@Component({
    selector: 'app-refuse-cancel-operation-modal',
    templateUrl: './refuse-cancel-operation-modal.component.html',
    styleUrls: ['./refuse-cancel-operation-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class RefuseCancelOperationModalComponent {
  isMutating = false;
  canValidate = false;
  title: string | undefined;
  message: string | undefined;

  comment: FormControl<string | null> = new FormControl<string>('', Validators.required);
  motifAnnulation: FormControl<MotifAnnulation | null> = new FormControl<MotifAnnulation | null>(
    null,
    Validators.required
  );
  motifRejet: FormControl<MotifRejet | null> = new FormControl<MotifRejet | null>(null, Validators.required);
  motifAnnulationOptions: OuiSelectOption<MotifAnnulation>[];
  motifRejetOptions: OuiSelectOption<MotifRejet>[];

  constructor(
    private dialogRef: MatDialogRef<RefuseCancelOperationModalComponent>,
    private optionsService: OptionsService,
    private queryManager: QueryManagerService,
    @Inject(MAT_DIALOG_DATA)
    protected data: {
      operationIds: number[];
      isReject: boolean;
    }
  ) {
    this.motifAnnulationOptions = this.optionsService.getMotifAnnulationOptions();

    this.motifRejetOptions = this.optionsService.getMotifRejetOptions();
  }

  ngOnInit() {
    if (this.data.isReject) {
      this.message = 'Précisez le motif du refus';
      this.title = "Refuser l'opération";
    } else {
      this.message = "Précisez le motif de l'annulation";
      this.title = "Annuler l'opération";
    }
    this.updateCanValidate();
    this.comment.valueChanges.subscribe(() => this.updateCanValidate());
    this.motifAnnulation.valueChanges.subscribe(() => this.updateCanValidate());
    this.motifRejet.valueChanges.subscribe(() => this.updateCanValidate());
  }

  updateCanValidate() {
    if (this.data.isReject) {
      this.canValidate = this.motifRejet.valid && this.comment.valid;
    } else {
      this.canValidate = this.motifAnnulation.valid && this.comment.valid;
    }
  }

  onCancel() {
    this.dialogRef.close();
  }

  async onValidate() {
    if (this.data.isReject) {
      if (this.motifRejet.valid && this.comment.valid) {
        var ope = await this.refuseOperations();
        this.dialogRef.close(ope);
      }
    } else {
      if (this.motifAnnulation.valid && this.comment.valid) {
        var ope = await this.cancelOperations();
        this.dialogRef.close(ope);
      }
    }
  }

  async cancelOperations(): Promise<Operation[] | undefined> {
    if (!this.data.operationIds?.length) {
      return undefined;
    }

    this.isMutating = true;

    try {
      const result = await firstValueFrom(
        this.queryManager.mutate<{ cancelOperations: Operation[] }>({
          mutation: cancelOperations,
          variables: {
            operationIds: this.data.operationIds,
            commentaire: this.comment.value,
            motifAnnulation: this.motifAnnulation.value,
          },
        })
      );
      return result.data?.cancelOperations;
    } finally {
      this.isMutating = false;
    }
  }

  async refuseOperations(): Promise<Operation[] | undefined> {
    if (!this.data.operationIds?.length) {
      return undefined;
    }

    this.isMutating = true;

    try {
      const result = await firstValueFrom(
        this.queryManager.mutate<{ refuseOperations: Operation[] }>({
          mutation: refuseOperations,
          variables: {
            operationIds: this.data.operationIds,
            commentaire: this.comment.value,
            motifRejet: this.motifRejet.value,
          },
        })
      );

      return result.data?.refuseOperations;
    } finally {
      this.isMutating = false;
    }
  }
}
